import React, { useEffect, useState } from 'react'
import RightPanel from 'components/RightPanel/RightPanel'
import { useLocation, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import AdminPanelCN from 'components/AdminPanelCN/AdminPanelCN'
import HeaderCN from 'components/HeaderCN/HeaderCN'
import { AssetPresentationsMenu } from 'features/Presentations/components/AssetPresentationsMenu'
import { AssetReportsButton } from 'features/Explorer/components/AssetReportsButton'
import SecondaryNavCN from 'components/SecondaryNavCN/SecondaryNavCN'
import SidebarLocationsRecent from 'components/Sidebars/SidebarLocationsRecent'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import Popup from 'components/Popup'
import MapLocationPopupCN from 'components/popups/MapLocationPopupCN'
import LocationActionSheet from './components/Locations/LocationActionSheet'
import { prepareLocationForSave } from './utils/prepareLocationForSave'
import RenderSubRoutes from 'components/RenderSubRoutes'
import getLocationCenterRoutes from './getRoutes/getLocationCenterRoutes'
import getLocationRoutes from './getRoutes/getLocationRoutes'
import { BusinessCard } from 'components/Card'
import routes from 'constants/routes'
import useMediaQuery from 'hooks/useMediaQuery'
import useCapabilities from 'hooks/useCapabilities'
import * as _ from 'styles/helpers'
import { initializeApiKey } from 'slices/apiKeysSlice'
import { initializeActiveLocations, initializeLocations, updateLocation } from 'slices/locationSlice'
import { initializeUsers } from 'slices/usersSlice'

const Locations_ShowCN = () => {
	const dispatch = useDispatch()
	const params = useParams()
	const { id } = params

	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
	const [backTitle, setBackTitle] = useState(null)
	const [backLink, setBackLink] = useState(null)
	const [openEditPopup, setOpenEditPopup] = useState(false)
	const [openMap, setOpenMap] = useState(false)
	const [recordForEdit, setRecordForEdit] = useState<any>(null)
	const [center, setCenter] = useState<number[]>([])
	const [mapTitle, setMapTitle] = useState<string | null>(null)

	const currentRoute = useLocation()
	const getBackLink = () => {
		if (currentRoute.state) {
			if (backTitle === null) {
				setBackTitle(currentRoute.state.backButtonTitle)
				setBackLink(currentRoute.state.backButtonLink)
			}
			return { title: currentRoute.state.backButtonTitle, link: currentRoute.state.backButtonLink }
		}
		if (backTitle !== null) {
			return { title: backTitle, link: backLink }
		}
		return { title: 'Locations', link: routes.LOCATIONS }
	}

	const { entity: apiKey } = useSelector((state: any) => state.apiKey)

	const userLogin = useSelector((state: any) => state.userLogin)
	const { userInfo } = userLogin
	const capabilities = useCapabilities()

	const { isLoading, isInitialized, location } = useSelector((state: any) => ({
		isLoading: state.locations.isLoading,
		isInitialized: state.locations.isInitialized,
		location: state.locations.entities[id],
	}))

	const { success: userListInitialized } = useSelector((state: any) => ({
		success: state.userList.isInitialized,
	}))

	useEffect(() => {
		if (!isInitialized) {
			dispatch(initializeActiveLocations())
		}
		if (!userListInitialized) {
			dispatch(initializeUsers())
		}
		dispatch(initializeApiKey('REACT_APP_GOOGLE_API_KEY'))
	}, [dispatch, id, isInitialized, userListInitialized])

	const openEdit = () => {
		setOpenEditPopup(true)
	}

	const handleEditLocation = async (values: any) => {
		const newLocation = await prepareLocationForSave(values)
		await dispatch(updateLocation(newLocation))
		await dispatch(initializeLocations())
	}

	const openInMap = () => {
		setCenter([location.latitude, location.longitude])
		setRecordForEdit(location)
		if (location.retailer) {
			setMapTitle(
				`${location.retailer} - ${location.assetName} - ${location.address}, ${location.city}, ${location.stateProvince}`
			)
		} else {
			setMapTitle(`${location.assetName} - ${location.address}, ${location.city}, ${location.stateProvince}`)
		}
		setOpenMap(true)
	}

	const isMobile = useMediaQuery(_.MQ.before_nav)

	if (!location || isLoading) {
		return null
	}

	let subRoutes: any[] = []
	if (location.assetType === 'center') {
		subRoutes = getLocationCenterRoutes(id, capabilities)
	} else {
		subRoutes = getLocationRoutes(id, capabilities)
	}

	return (
		<div className="flex h-screen overflow-hidden bg-gray-50">
			<AdminPanelCN mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />

			<div className="flex-1 flex flex-col overflow-hidden">
				<HeaderCN onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />

				<div className="flex-shrink-0 border-b border-gray-200 bg-white px-6 py-2 flex items-center gap-3">
					<Avatar className="h-8 w-8">
						<AvatarImage src={location?.image} alt={location?.assetName} />
						<AvatarFallback className="text-xs bg-gray-200 text-gray-600">
							{(location?.assetName || '?').charAt(0).toUpperCase()}
						</AvatarFallback>
					</Avatar>
					{location?.owner?._id === userInfo?._id ? (
						<button
							type="button"
							onClick={openEdit}
							className="font-semibold text-sm text-[#0080fe] bg-transparent border-none p-0 m-0 cursor-pointer hover:underline"
						>
							{location?.assetName}
						</button>
					) : (
						<span className="font-semibold text-sm text-gray-800">{location?.assetName}</span>
					)}
					<button
						type="button"
						onClick={openInMap}
						className="text-sm text-[#0080fe] bg-transparent border-none p-0 m-0 cursor-pointer hover:underline"
					>
						{location?.address}, {location?.city}, {location?.stateProvince}
					</button>
					<span className="text-sm text-gray-500">Location Originator: {location?.owner?.name}</span>
					{/* Decks with a story about this location (MYD-497). Renders nothing when there are none. */}
					<div className="ml-auto flex items-center gap-2">
						{/* Reports from a template, narrowed to this location (MYD-598). */}
						<AssetReportsButton type="location" entityId={id} name={location?.assetName} />
						<AssetPresentationsMenu scope="location" entityId={id} />
					</div>
				</div>

				<SecondaryNavCN links={subRoutes} backLink={getBackLink()} sourceName="Locations" />

				<div className="flex-1 flex overflow-hidden">
					<main className="flex-1 overflow-y-auto p-6">
						{isMobile && !isLoading && (
							<div style={{ marginBottom: '24px' }}>
								<BusinessCard
									isHorizontal
									image={location?.image}
									name={location?.assetName}
									address1={location?.address}
									address2={`${location?.city}, ${location?.stateProvince}`}
								/>
							</div>
						)}
						<RenderSubRoutes routes={subRoutes} state={currentRoute.state} />
					</main>

					<RightPanel storageKey="location" label="Location panel" className="overflow-y-auto">
						<SidebarLocationsRecent />
					</RightPanel>
				</div>
			</div>

			<LocationActionSheet
				location={location}
				open={openEditPopup}
				onClose={() => setOpenEditPopup(false)}
				onEditLocation={handleEditLocation}
				showActions={false}
			/>
			{openMap && recordForEdit && (
				<MapLocationPopupCN
					open={openMap}
					onClose={() => setOpenMap(false)}
					apiKey={apiKey?.key}
					location={recordForEdit}
				/>
			)}
		</div>
	)
}

export default Locations_ShowCN
